import { defineComponent as _defineComponent } from 'vue'
import { computed, watch, ref, reactive } from 'vue';
import imageApi from '@/api/image';
import lastReadApi from '@/api/last-read';
import {
	global_get_array,
	global_set,
	global_set_json,
	window_go_top,
} from '@/utils';
import { ElMessage as msg } from 'element-plus';
import { config } from '@/store';
import historyApi from '@/api/history';
import i18n from '@/i18n';
import { onMounted } from 'vue';
import chapterListMenu from './components/chapter-list-menu.vue';
import rightSidebar from './components/right-sidebar.vue';
import bookmark from './components/bookmark.vue';
import pageNumber from './components/page-number.vue';
import { useRoute, useRouter } from 'vue-router';
import { chapterInfoType } from '@/type/chapter';
import chapterApi from '@/api/chapter';

const __default__ = { name: 'browse-views' }

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const { t } = i18n.global;
const route = useRoute();
const router = useRouter();

// 图片文件列表
let imgFileList = ref<string[]>([]);
// 图片路径列表
let imgPathList = ref<string[]>([]);
// 当前图片页码
let page = 1;
// 初始加载页码数量
const initPage = 3;
// 是否正在加载图片
const loading = ref(false);
// 是否加载完全部图片
let finished = ref(false);

const flowList = ref();

const chapterList = computed(() => { return global_get_array('chapterList') })

const index = computed<number>(() => {
	const list = chapterList.value;
	const chapterId = Number(route.query.chapterId);

	for (let i = 0; i < list.length; i++) {
		if (chapterId === list[i].chapterId) {
			//缓存章节坐标
			global_set('chapterIndex', i);
			return i;
		}
	}

	return -1;
})

// 章节详情
let chapterInfo = reactive<chapterInfoType>({
	chapterId: 0,
	chapterPath: '',
	chapterType: 'img',
	browseType: '',
	chapterCover: '',
	chapterName: '',
	createTime: '',
	mangaId: 0,
	mediaId: 0,
	pathId: 0,
	picNum: 0,
	updateTime: '',
})

// 正在浏览的页码
let currentPage = ref(1);

// 在中途加载 前置没有加载的页面数量
let beforeBookMark = 0;

// 存储进度
watch(
	() => currentPage.value,
	() => {
		lastReadApi.add(currentPage.value, chapterInfo.chapterId, chapterInfo.mangaId, finished.value);
	}
)

/**
 * 加载图片
 */
async function page_change() {
	// 无数据 退出
	if (!imgPathList.value.length) {
		loading.value = false;
		return false;
	}

	// 加载页面并等待返回
	await load_image(page - 1);

	// 是否加载完全部
	finished.value = page >= imgPathList.value.length;

	// 页码递增
	++page;

	// 是否完成页面初始化加载,未完成则再次加载图片
	page < initPage && setTimeout(async () => {
		await page_change()
	}, 1000);
}

/**
 * @description: 上一页
 * @return {*}
 */
async function before_page() {
	if (beforeBookMark === 0) {
		loading.value = false;
		return;
	}

	// 向前加载一页
	await load_image(--beforeBookMark, 0, true);

	// 重新计算当前页码
	scroll_page();

	// 加载结束,更新状态
	loading.value = false;
}

/**
 * @description: 加载图片
 * @param {*} index
 * @param {*} errNum
 * @param {*} unshift
 * @return {*}
 */
async function load_image(index: number, errNum = 0, unshift = false) {
	// 重新请教超过三次 取消此图片加载
	if (errNum > 3) return false;

	const [res, err] = await imageApi.chapter_img(imgPathList.value[index], index + 1, chapterInfo.chapterId, chapterInfo.mangaId).then(res => [res, null]).catch(err => [null, err]);

	// 错误处理
	if (err) {
		setTimeout(() => {
			load_image(index, errNum + 1);
		}, 1000)
		return;
	}

	if (unshift) {
		imgFileList.value.unshift(res);
	} else {
		imgFileList.value[index - beforeBookMark] = res;
	}

	// 加载结束,更新状态
	loading.value = false;

	return true;
}

/**
 * 重载页面
 */
async function reload_page(addHistory = true, clearPage = true, pageParams = 1) {
	// 初始化chapterInfo
	if (!chapterInfo.chapterId) {
		const chapterId = Number(route.query.chapterId);

		// 获取章节信息
		chapterInfo = chapterList.value.filter((item: chapterInfoType) => item.chapterId == chapterId)[0]

		// 更新阅读记录
		lastReadApi.add(currentPage.value, chapterInfo.chapterId, chapterInfo.mangaId);
	}

	// 重置图片数据
	if (clearPage) {
		imgPathList.value = [];
		imgFileList.value = [];
		beforeBookMark = pageParams - 1;
		currentPage.value = pageParams;
		page = pageParams;
		// 重置滚动条
		window_go_top();
	}

	if (addHistory) {
		historyApi.add_history();
	}

	const res = await chapterApi.get_images(chapterInfo.chapterId);

	switch (res.state) {
		case 'uncompressed':
			setTimeout(() => {
				reload_page(false);
			}, 2000);
			break;
		case 'compressing':
			// 进度有所增加 则更新图片列表
			if (res.list.length > imgFileList.value.length) {
				imgPathList.value = res.list;
				finished.value = false;
				page_change();
			}
			// 再次加载解压进度
			setTimeout(() => {
				reload_page(false, false);
			}, 2000);
			break;
		case 'compressed':
			imgPathList.value = res.list;
			page_change();
			break;
		default:
			imgPathList.value = res.list;
			page_change();
	}

	global_set_json('imgPathList', imgPathList.value);
}

function load_again() {
	finished.value = false;
}

/**
 * 上一页
 * */
async function before() {
	if (index.value == 0) {
		msg(t('page.firstChapter'));
		return false;
	}

	if (!index.value) return;

	chapterInfo = chapterList.value[index.value - 1];

	await router.push({
		name: route.name as string,
		query: Object.assign({}, route.query, { chapterId: chapterInfo.chapterId, }),
		params: { page: 1 },
	});

	update_chapter_info();

	/**
	 * 刷新页面
	 * 自定义重载方法没有重置滚动条,导致vant list不断触发触底事件
	 * 因此暂时采用刷新页面的方式解决
	 */
	reload_page(true, true, 1);
}

/**
 * 下一页
 * */
async function next() {
	if (index.value == chapterList.value.length - 1) {
		msg(t('page.lastChapter'));
		return false;
	}

	chapterInfo = chapterList.value[index.value + 1];

	await router.push({
		name: route.name as string,
		query: Object.assign({}, route.query, { chapterId: chapterInfo.chapterId, }),
		params: { page: 1 },
	});

	update_chapter_info();

	// 刷新页面
	reload_page(true, true, 1);
}
/**
 * 选择章节
 * @param index
 */
async function change_chapter(index: number) {
	chapterInfo = chapterList.value[index];
	await router.push({
		name: route.name as string,
		query: Object.assign({}, route.query, {
			chapterId: chapterInfo.chapterId,
		}),
	});

	update_chapter_info();

	// 重载页面
	reload_page(true, true, 1);
}

function update_chapter_info() {
	if (!chapterInfo.chapterId) return;
	global_set('chapterId', chapterInfo.chapterId);
	global_set('chapterName', chapterInfo.chapterName);
	global_set('chapterPath', chapterInfo.chapterPath);
	global_set('chapterCover', chapterInfo.chapterCover);
}

// 阅读状态控制
function switch_menu() {
	config.browseTop = !config.browseTop;
}

/**
 * @description: 绑定滚动事件 用以更新页码
 * @return {*}
 */
function scroll_page() {
	const flowListDom = flowList.value;
	const scrollY = window.scrollY;

	if (!flowListDom) return 0;

	let imgs = flowListDom.getElementsByTagName('img');

	for (let i = 0; i < imgs.length; i++) {
		if (scrollY <= imgs[i].offsetTop) {
			currentPage.value = i + beforeBookMark + 1;
			const pageImage = imgPathList.value[page - 1];

			global_set('page', currentPage.value);
			global_set('pageImage', pageImage);

			return;
		}
	}
}

/**
 * 下载当前图片
 */
function dwonload_image() {
	// 获取当前图片
	const src = imgFileList.value[currentPage.value - 1];

	const a = document.createElement('a');
	a.href = src;
	a.download = 'smangaImage.png';
	a.click();
}

// 生命周期
onMounted(() => {
	// 设置浏览模式
	config.browseType = 'flow';

	let page = Number(route.params.page) || 1;

	// 部分旧代码将页码设置为0或者-1 这里做下更正
	if (page < 1) page = 1;

	// 加载页面
	const notAddHistory = route.params.notAddHistory || false;

	reload_page(!notAddHistory, true, page);

	window.addEventListener('scroll', scroll_page);
})



const __returned__ = { t, route, router, get imgFileList() { return imgFileList }, set imgFileList(v) { imgFileList = v }, get imgPathList() { return imgPathList }, set imgPathList(v) { imgPathList = v }, get page() { return page }, set page(v) { page = v }, initPage, loading, get finished() { return finished }, set finished(v) { finished = v }, flowList, chapterList, index, get chapterInfo() { return chapterInfo }, set chapterInfo(v) { chapterInfo = v }, get currentPage() { return currentPage }, set currentPage(v) { currentPage = v }, get beforeBookMark() { return beforeBookMark }, set beforeBookMark(v) { beforeBookMark = v }, page_change, before_page, load_image, reload_page, load_again, before, next, change_chapter, update_chapter_info, switch_menu, scroll_page, dwonload_image, get config() { return config }, chapterListMenu, rightSidebar, bookmark, pageNumber }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})